import React from 'react';

const SKEL_STYLE = `
@keyframes bc-skel-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

/* Responsive concierge shell skeleton:
   - Desktop: left sidebar + content
   - Mobile: top header (hamburger) + content
*/
.bc-concierge-skel-shell{ position:relative; display:flex; flex-direction:row; height:100dvh; max-height:100dvh; min-height:-webkit-fill-available; overflow:hidden; background:#fff; box-sizing:border-box; }
.bc-concierge-skel-desktopSidebar{ display:flex; }
.bc-concierge-skel-mobileHeader{ display:none; }
.bc-concierge-skel-desktopContent{ display:flex; flex:1; min-width:0; min-height:0; flex-direction:column; overflow:hidden; }
.bc-concierge-skel-mobileContent{ display:none; flex:1; min-width:0; min-height:0; flex-direction:column; overflow:hidden; }
.bc-concierge-skel-contentShell{ display:flex; flex:1; min-width:0; min-height:0; flex-direction:column; overflow:hidden; }
.bc-concierge-skel-contentDesktopOnly{ display:flex; flex:1; min-width:0; min-height:0; flex-direction:column; overflow:hidden; }
.bc-concierge-skel-contentMobileOnly{ display:none; flex:1; min-width:0; min-height:0; flex-direction:column; overflow:hidden; }
.bc-concierge-skel-footerHint{ position:absolute; bottom:24px; left:0; right:0; display:flex; justify-content:center; pointer-events:none; }

@media (max-width: 767px){
  .bc-concierge-skel-shell{ flex-direction:column; }
  .bc-concierge-skel-desktopSidebar{ display:none; }
  .bc-concierge-skel-mobileHeader{ display:flex; }
  .bc-concierge-skel-desktopContent{ display:none; }
  .bc-concierge-skel-mobileContent{ display:flex; }
  .bc-concierge-skel-contentDesktopOnly{ display:none; }
  .bc-concierge-skel-contentMobileOnly{ display:flex; }
  .bc-concierge-skel-footerHint{ display:none; }
}
`;
const PULSE = 'bc-skel-pulse 2.8s cubic-bezier(0.4, 0, 0.2, 1) infinite';

const skel = (
  width: number | string,
  height: number,
  radius: number,
  delay = '0s',
  bg = '#f0f0f0',
): React.CSSProperties => ({
  width,
  height,
  backgroundColor: bg,
  borderRadius: radius,
  animation: PULSE,
  animationDelay: delay,
});

const MOBILE_BG = '#ffffff';
const MOBILE_NAV_BG = '#ffffff';
const CARD_BG = '#f8f8f8';

const pillShell = (width: number | string, delay = '0s'): React.ReactElement => (
  <div style={skel(width, 40, 20, delay)} />
);

export const ConciergeDesktopContentSkeleton: React.FC = () => (
  <div
    style={{
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}
  >
    <div style={{ flex: '1 1 0', minHeight: 0 }} />
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: 24,
        flexShrink: 0,
      }}
    >
      <div style={skel(40, 40, 20)} />
      <div style={skel(140, 18, 9, '0.05s')} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <div style={skel(320, 24, 12, '0.1s')} />
        <div style={skel(260, 24, 12, '0.15s')} />
      </div>
      <div
        style={{
          ...skel('100%', 100, 16, '0.2s'),
          maxWidth: 560,
          backgroundColor: '#f8f8f8',
          marginTop: 8,
        }}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 4 }}>
        {[180, 220, 160, 200].map((w, i) => (
          <div key={i} style={skel(w, 40, 20, `${0.25 + 0.05 * i}s`)} />
        ))}
      </div>
    </div>
    <div style={{ flex: '1 1 0', minHeight: 0 }} />
  </div>
);

// Backwards-compatible export used by other widgets.
export const ConciergeContentSkeleton: React.FC = () => (
  <div className="bc-concierge-skel-contentShell">
    <style>{SKEL_STYLE}</style>
    <div className="bc-concierge-skel-contentDesktopOnly">
      <ConciergeDesktopContentSkeleton />
    </div>
    <div className="bc-concierge-skel-contentMobileOnly">
      <ConciergeMobileWelcomeSkeleton />
    </div>
  </div>
);

export const ConciergeSidebarSkeleton: React.FC = () => (
  <div
    style={{
      width: 48,
      flexShrink: 0,
      borderRight: '1px solid #e8e8ed',
      alignSelf: 'stretch',
      minHeight: 0,
      boxSizing: 'content-box',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 24,
      gap: 16,
    }}
  >
    <div style={skel(24, 24, 12)} />
    <div style={{ height: 8 }} />
    <div style={skel(24, 24, 6, '0.1s')} />
    <div style={skel(24, 24, 6, '0.2s')} />
  </div>
);

/**
 * Mirrors `common-ui` Concierge mobile nav structure:
 * hamburger (left) + centered brand logo + close/share (right).
 */
export const ConciergeMobileNavSkeleton: React.FC = () => (
  <div
    style={{
      height: 80, // mirrors ConciergeHomeMobileNavWidget default navHeight
      flexShrink: 0,
      boxSizing: 'border-box',
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '24px 24px', // mirrors default navPaddingY/X
      backgroundColor: MOBILE_NAV_BG,
    }}
  >
    <div style={skel(20, 20, 6)} />
    <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', padding: '0 8px' }}>
      <div style={skel(79, 18, 6, '0.08s')} />
    </div>
    <div style={skel(20, 20, 6, '0.14s')} />
  </div>
);

/**
 * Mirrors `common-ui` `ConciergeWelcomeFullPage` mobile layout:
 * centered hero + bottom starters chips + bottom input bar.
 */
export const ConciergeMobileWelcomeSkeleton: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      flex: 1,
      minHeight: 0,
      boxSizing: 'border-box',
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: MOBILE_BG,
    }}
  >
    <div style={{ flex: 1 }} />

    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '16px 16px',
        flexShrink: 0,
      }}
    >
      <div style={skel(24, 24, 12)} />
      <div style={{ height: 14 }} />
      <div style={skel(52, 16, 8, '0.05s')} />
      <div style={{ height: 10 }} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={skel('min(84vw, 360px)', 22, 11, '0.1s')} />
        <div style={skel('min(76vw, 300px)', 22, 11, '0.15s')} />
      </div>
    </div>

    <div style={{ flex: 1 }} />

    {/* Mobile starters chips (above input) */}
    <div style={{ flexShrink: 0, padding: '0 8px 0 8px' }}>
      <div style={{ display: 'flex', gap: 10, overflow: 'hidden', padding: '0 8px' }}>
        {pillShell(244, '0.22s')}
        {pillShell(198, '0.28s')}
        {pillShell(186, '0.34s')}
      </div>
    </div>

    {/* Mobile input bar pinned to bottom */}
    <div
      style={{
        flexShrink: 0,
        paddingTop: 16,
        paddingLeft: 8,
        paddingRight: 8,
        paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
      }}
    >
      <div
        style={{
          width: '100%',
          height: 100,
          maxWidth: 900,
          padding: '14px 14px 12px 14px',
          borderRadius: 18,
          backgroundColor: CARD_BG,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div style={skel(98, 12, 6, '0.18s')} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <div style={skel(18, 18, 9, '0.22s')} />
          <div style={skel(18, 18, 9, '0.26s')} />
          <div style={skel(20, 20, 10, '0.3s')} />
        </div>
      </div>
    </div>
  </div>
);

export const ConciergeFullPageSkeleton: React.FC = () => (
  <div className="bc-concierge-skel-shell">
    <style>{SKEL_STYLE}</style>
    <div className="bc-concierge-skel-mobileHeader">
      <ConciergeMobileNavSkeleton />
    </div>
    <div className="bc-concierge-skel-desktopSidebar">
      <ConciergeSidebarSkeleton />
    </div>
    <div className="bc-concierge-skel-desktopContent">
      <ConciergeDesktopContentSkeleton />
    </div>
    <div className="bc-concierge-skel-mobileContent">
      <ConciergeMobileWelcomeSkeleton />
    </div>
    <div className="bc-concierge-skel-footerHint">
      <div style={skel(120, 20, 6, '0.4s')} />
    </div>
  </div>
);

/**
 * Mobile chat-shaped content skeleton: edge-to-edge messages + bottom input.
 * Mirrors `ConciergeChatAreaV2` mobile layout while a conversation hydrates.
 */
const ConciergeMobileChatContentSkeleton: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      flex: 1,
      minHeight: 0,
      boxSizing: 'border-box',
      overflow: 'hidden',
      backgroundColor: MOBILE_BG,
    }}
  >
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '16px 16px 0',
        flexShrink: 0,
      }}
    >
      {/* User message bubble (right-aligned) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={skel('min(60%, 260px)', 44, 22)} />
      </div>
      {/* Assistant message lines (left-aligned) */}
      <div style={skel('min(85%, 520px)', 56, 12, '0.1s')} />
      <div style={skel('min(70%, 420px)', 56, 12, '0.18s')} />
    </div>

    <div style={{ flex: 1 }} />

    {/* Input bar pinned to bottom */}
    <div
      style={{
        flexShrink: 0,
        paddingTop: 16,
        paddingLeft: 8,
        paddingRight: 8,
        paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
      }}
    >
      <div
        style={{
          width: '100%',
          height: 56,
          maxWidth: 900,
          padding: '14px 14px 12px 14px',
          borderRadius: 28,
          backgroundColor: CARD_BG,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={skel(140, 14, 7, '0.18s')} />
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={skel(20, 20, 10, '0.22s')} />
          <div style={skel(20, 20, 10, '0.26s')} />
          <div style={skel(20, 20, 10, '0.3s')} />
        </div>
      </div>
    </div>
  </div>
);

/**
 * Desktop chat-shaped content skeleton: messages + input bar are constrained
 * to a centered max-width 768px column to mirror `MessageListV2` /
 * `ChatInput` layout on wide viewports.
 */
const ConciergeDesktopChatContentSkeleton: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      flex: 1,
      minHeight: 0,
      boxSizing: 'border-box',
      overflow: 'hidden',
      backgroundColor: MOBILE_BG,
    }}
  >
    {/* Centered conversation column */}
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        padding: '32px 24px 0',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 768,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        {/* User message bubble (right-aligned) */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={skel('min(60%, 320px)', 48, 24)} />
        </div>
        {/* Assistant message lines (left-aligned, full column width) */}
        <div style={skel('100%', 72, 12, '0.1s')} />
        <div style={skel('80%', 56, 12, '0.18s')} />
      </div>
    </div>

    {/* Input bar centered at bottom (compact preset: ~56px pill) */}
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'center',
        padding: '16px 24px 24px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 768,
          height: 56,
          padding: '0 16px',
          borderRadius: 28,
          backgroundColor: CARD_BG,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={skel(180, 14, 7, '0.18s')} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={skel(22, 22, 11, '0.22s')} />
          <div style={skel(22, 22, 11, '0.26s')} />
          <div style={skel(28, 28, 14, '0.3s')} />
        </div>
      </div>
    </div>
  </div>
);

/**
 * Full-page chat skeleton mirroring `ConciergeFullPageSkeleton`'s shell
 * (sidebar/header) but using chat-shaped content instead of the welcome
 * layout. Use this as the Suspense fallback when the URL points to an
 * existing conversation (e.g. `?conversationId=…`).
 */
export const ConciergeChatPageSkeleton: React.FC = () => (
  <div className="bc-concierge-skel-shell">
    <style>{SKEL_STYLE}</style>
    <div className="bc-concierge-skel-mobileHeader">
      <ConciergeMobileNavSkeleton />
    </div>
    <div className="bc-concierge-skel-desktopSidebar">
      <ConciergeSidebarSkeleton />
    </div>
    <div className="bc-concierge-skel-desktopContent">
      <ConciergeDesktopChatContentSkeleton />
    </div>
    <div className="bc-concierge-skel-mobileContent">
      <ConciergeMobileChatContentSkeleton />
    </div>
  </div>
);
